import { ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { routes } from './app.routes';
import { provideAuth } from './core/auth/auth.providers';
import { provideAithFilters } from './core/filters/filters.providers';
import { provideNavigationLogging } from './core/logging/navigation-logging.providers';
import { provideAithNotifications } from './core/notifications/notification.providers';
import { provideRealtime } from './core/realtime/realtime.providers';
import { provideSession } from './core/session/session.providers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideSession(),
    // AITH-43: identity is eager — session derivation and the route guards run
    // at startup. In mockAuth dev mode the OIDC library registers with zero
    // configurations and stays inert (DevSessionService stands in).
    provideAuth(),
    provideNavigationLogging(),
    provideRealtime(),
    // AITH-44: one root-provided shared filter state per session (FR-009). The
    // abstract FilterScopeService port is bound here; the bar's controls use
    // the controller, subscribed views read the port.
    //
    // The INITIALIZER PHASE BOUNDARY is load-bearing since AITH-121 made
    // sign-in a redirect; provider-array order is not.
    // provideAithFilters registers an ENVIRONMENT initializer, which runs while
    // the injector is built; provideAuth registers an APP_INITIALIZER, which
    // runs after. So FilterUrlProjection.initialize() always happens with
    // router.navigated still false and binds to the first NavigationStart —
    // and that first navigation is completeBootstrapCallback's
    // navigateByUrl(returnUrl), i.e. the user's pre-authentication destination
    // rather than the `/sign-in?code=…` document the redirect landed on.
    // The first navigation must remain the restored destination, not the
    // callback URL, or deep links behind sign-in lose their filters (FR-012,
    // SC-002). AITH-44's standing guardrail and two focused specs pin this.
    provideAithFilters(),
    // R-009 locale seam: the viewer's locale flows to the formatting utilities
    // (src/app/core/format/) via LOCALE_ID; unit tests override it per case.
    { provide: LOCALE_ID, useFactory: () => navigator.language },
    // T037 (R-008 spike): the toast stack is global, eager, and small.
    // The facade (NotificationService) enforces conventions; the adapter renders.
    provideAnimationsAsync(),
    provideAithNotifications(),
  ],
};
